import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { IROH_SERVICES_API_PREFIX } from "@/api/domain"
import { SortDir } from "@/api/routes/staff"
import { Tag } from "@/api/tags"
import type { IrohNode, ProjectIdParam } from "@/types"

export { SortDir }

/// The reserved name used by the backend for the auto-created cloud endpoint
/// on every project. See CLOUD_NODE_NAME in backend/src/model/iroh_node.rs.
export const CLOUD_NODE_NAME = "cloud node"

export enum IrohNodeSortColumn {
  Name = "name",
  LastAccess = "last_access",
  AuthTokenExpiresAt = "auth_token_expires_at",
}

// Define a service using a base URL and expected endpoints
export const irohNodesApi = createApi({
  reducerPath: "irohNodesApi",
  tagTypes: [Tag.IrohNodes],
  baseQuery: fetchBaseQuery({
    baseUrl: `${IROH_SERVICES_API_PREFIX}iroh-nodes`,
    mode: "cors",
    credentials: "include",
  }),
  endpoints: (build) => ({
    irohNodesGet: build.query<
      IrohNode[],
      ProjectIdParam & {
        offset: number
        limit: number
        q?: string
        sort_by?: IrohNodeSortColumn
        sort_dir?: SortDir
      }
    >({
      query: ({ project_id, offset, limit, q, sort_by, sort_dir }) => {
        const params = new URLSearchParams()
        params.set("offset", String(offset))
        params.set("limit", String(limit))
        if (q) params.set("q", q)
        if (sort_by) params.set("sort_by", sort_by)
        if (sort_dir) params.set("sort_dir", sort_dir)
        return { url: `${project_id}?${params.toString()}` }
      },
      providesTags: (result, _err, { project_id }) =>
        result
          ? [
              ...result.map(({ node_id }) => ({
                type: Tag.IrohNodes,
                id: node_id,
              })),
              { type: Tag.IrohNodes, id: project_id },
            ]
          : [{ type: Tag.IrohNodes, id: project_id }],
    }),
    irohNodeGet: build.query<IrohNode, ProjectIdParam & { node_id: string }>({
      query: ({ project_id, node_id }) => `${project_id}/${node_id}`,
      providesTags: (result) =>
        result ? [{ type: Tag.IrohNodes, id: result.node_id }] : [],
    }),
    irohNodeDelete: build.mutation<void, ProjectIdParam & { node_id: string }>({
      query: ({ project_id, node_id }) => ({
        url: `${project_id}/${node_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _err, { project_id, node_id }) => [
        { type: Tag.IrohNodes, id: node_id },
        { type: Tag.IrohNodes, id: project_id },
      ],
    }),
  }),
})

export const {
  useIrohNodesGetQuery,
  useIrohNodeGetQuery,
  useIrohNodeDeleteMutation,
} = irohNodesApi
